import {
  ColumnDef,
  ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  TableMeta,
  useReactTable,
  Column,
  Header,
  Cell,
  ColumnSizingState,
} from "@tanstack/react-table";
import { ArrowDownUp, MoveDown, MoveUp } from "lucide-react";
import React from "react";
import { cn } from "../../../utils";
import { Typography } from "../Typography";
import { useTranslation } from "react-i18next";
import { useTheme } from "../ThemeToggler";

export type TCommonTableMeta = {
  thClass?: string;
  tdClass?: string;
  fixedth?: string;
};

export type TCommonTableProps<TData> = {
  dataTestId?: string;
  columns: ColumnDef<any, any>[];
  rows: any[];
  onClickRow?: () => void;
  totalRecordCount: number;
  pagination?: PaginationState;
  setPagination?: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
  manualSorting?: boolean;
  setSorting?: (
    updater: SortingState | ((old: SortingState) => SortingState),
  ) => void;
  sorting?: SortingState;
  id?: string;
  meta?: TableMeta<TData> & TCommonTableMeta;
  renderExpandedRow?: (row: TData, colSpan?: number) => React.ReactNode;
  tableClassName?: string;
  trClassName?: string;
  RowComponent?: React.FC<{ row: TData; cells: React.ReactNode[] }>;
  tableMaxHeight?: string;
  initialColumnPinning?: ColumnPinningState;
  enableColumnResizing?: boolean;
};

function CommonTable<TData extends { id: string }>({
  dataTestId,
  columns,
  rows,
  totalRecordCount,
  pagination,
  setPagination,
  setSorting,
  sorting,
  meta,
  renderExpandedRow,
  manualSorting,
  tableClassName,
  trClassName,
  RowComponent,
  tableMaxHeight,
  initialColumnPinning,
  id,
  enableColumnResizing = true,
}: TCommonTableProps<any>) {
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({});
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(
    initialColumnPinning ?? { left: [], right: [] },
  );
  const { theme } = useTheme();
  const reactTable = useReactTable<any>({
    columns,
    data: rows ?? [],
    pageCount: Math.ceil(totalRecordCount / (pagination?.pageSize ?? 10)) ?? -1,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: manualSorting ?? false,
    rowCount: totalRecordCount,
    getRowCanExpand: () => true,
    onPaginationChange: setPagination,
    state: {
      pagination,
      sorting,
      columnPinning,
      columnSizing,
    },
    onColumnPinningChange: setColumnPinning,
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    getSortedRowModel: getSortedRowModel(),
    enableColumnResizing,
    columnResizeMode: "onChange",
    sortDescFirst: false,
    meta,
  });

  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const handleNavigation = () => {
    // your navigation logic
  };

  const handleSort = (columnId: string, direction: "asc" | "desc" | "") => {
    if (setSorting) {
      if (direction === "") {
        setSorting([]);
      } else {
        setSorting([{ id: columnId, desc: direction === "desc" }]);
      }
    }
  };

  const handleHeaderSortCycle = (columnId: string) => {
    if (!setSorting) return;
    const column = reactTable.getColumn(columnId);
    const current = column?.getIsSorted();
    const next: "asc" | "desc" | "" =
      current === false ? "asc" : current === "asc" ? "desc" : "";
    handleSort(columnId, next);
  };

  // Helper function to get sticky positioning styles
  const getCommonPinningStyles = (
    column: Column<TData>,
  ): React.CSSProperties => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
      isPinned === "right" && column.getIsFirstColumn("right");

    return {
      position: isPinned ? "sticky" : "relative",
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
      width: column.getSize(),
      minWidth: column.columnDef.minSize,
      maxWidth: column.columnDef.maxSize,
      zIndex: isPinned ? 10 : 0,
      opacity: 1,
      boxShadow: isLastLeftPinnedColumn
        ? theme === "dark"
          ? "2px 0 4px -2px rgba(255, 255, 255, 0.1)"
          : "2px 0 4px -2px rgba(0, 0, 0, 0.1)"
        : isFirstRightPinnedColumn
          ? theme === "dark"
            ? "-2px 0 4px -2px rgba(255, 255, 255, 0.1)"
            : "-2px 0 4px -2px rgba(0, 0, 0, 0.15)"
          : undefined,
    };
  };

  return (
    <div className="w-full">
      <div
        className={cn(
          tableMaxHeight ?? "max-h-[calc(100vh-272px)] ",
          "overflow-auto",
        )}
        data-testid="table-scroll-container"
        data-max-height={tableMaxHeight ?? "default"}
      >
        <table
          data-testid={dataTestId}
          className={cn(
            "w-full border-separate border-spacing-0",
            tableClassName,
          )}
          style={{
            minWidth: "100%",
            width: reactTable.getCenterTotalSize(),
            tableLayout: "fixed",
          }}
        >
          <thead className="border-y border sticky top-0 z-20 divide-gray">
            {reactTable.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(
                  (header: Header<TData, unknown>, index: number) => {
                    const vMets = header.column.columnDef
                      .meta as TCommonTableMeta;
                    const pinningStyles = getCommonPinningStyles(header.column);

                    return (
                      <th
                        key={`col_${header.id}_${index}`}
                        colSpan={header.colSpan}
                        style={pinningStyles}
                        className={cn(
                          `group px-2 py-2 text-foreground bg-white dark:bg-[#22344a] border-y dark:border-white/10 relative ${vMets?.fixedth ?? ""}`,
                        )}
                      >
                        {!header.isPlaceholder && (
                          <div
                            className={`flex items-center cursor-pointer ${header.column.getCanSort() ? " select-none" : ""} ${isRTL ? "text-right" : "text-left"}`}
                            onClick={() =>
                              header.column.getCanSort() &&
                              handleHeaderSortCycle(header.column.id)
                            }
                            onKeyDown={(e) => {
                              if (
                                (e.key === "Enter" || e.key === " ") &&
                                header.column.getCanSort()
                              ) {
                                handleHeaderSortCycle(header.column.id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            title={
                              header.column.getCanSort()
                                ? header.column.getIsSorted() === false
                                  ? "Sort ascending"
                                  : header.column.getIsSorted() === "asc"
                                    ? "Sort descending"
                                    : "Clear sort"
                                : undefined
                            }
                          >
                            <Typography
                              variant={"article"}
                              affects={"semiBold"}
                              className={`flex items-center text-sm uppercase gap-2 ${vMets?.thClass ?? ""}`}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                              {header.column.getCanSort() ? (
                                header.column.getIsSorted() ? (
                                  header.column.getIsSorted() === "asc" ? (
                                    <MoveUp
                                      size={11}
                                      className="shrink-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSort(header.column.id, "desc");
                                      }}
                                      id={`${id}-sort-asc-icon`}
                                      aria-label={`Sort ${header.column.id} ascending`}
                                    />
                                  ) : (
                                    <MoveDown
                                      size={11}
                                      className="shrink-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSort(header.column.id, "");
                                      }}
                                      id={`${id}-sort-desc-icon`}
                                      aria-label={`Sort ${header.column.id} descending`}
                                    />
                                  )
                                ) : (
                                  <ArrowDownUp
                                    size={12}
                                    className="shrink-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSort(header.column.id, "asc");
                                    }}
                                    id={`${id}-sort-none-icon`}
                                    aria-label={`Sort ${header.column.id}`}
                                  />
                                )
                              ) : null}
                            </Typography>
                          </div>
                        )}
                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={cn(
                              "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none hover:bg-primary/30 group-hover:bg-primary/10 transition-colors",
                              header.column.getIsResizing()
                                ? "bg-primary w-1"
                                : "bg-transparent",
                            )}
                          />
                        )}
                      </th>
                    );
                  },
                )}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray dark:divide-gray">
            {reactTable.getRowModel().rows.map((row) => {
              const cells = row
                .getVisibleCells()
                .map((cell: Cell<TData, unknown>, index: number) => {
                  const vMetsTd = cell.column.columnDef
                    .meta as TCommonTableMeta;
                  const pinningStyles = getCommonPinningStyles(cell.column);

                  return (
                    <td
                      key={`col_${cell.id}_${index}`}
                      style={pinningStyles}
                      onClick={() =>
                        cell.column.columnDef.header !== "action" &&
                        handleNavigation()
                      }
                      onKeyDown={(e) => {
                        if (
                          (e.key === "Enter" || e.key === " ") &&
                          cell.column.columnDef.header !== "action"
                        ) {
                          handleNavigation();
                        }
                      }}
                      role="cell"
                      tabIndex={0}
                      className={`px-2 py-2 text-sm text-foreground whitespace-pre-wrap break-all border-b bg-background dark:border-white/10 dark:bg-[#22344a] relative ${
                        vMetsTd?.tdClass ?? ""
                      }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  );
                });

              return (
                <React.Fragment key={String(row.id)}>
                  {RowComponent ? (
                    <RowComponent row={row} cells={cells} />
                  ) : (
                    <tr
                      key={`row_${String(row.id)}`}
                      className={cn(trClassName)}
                    >
                      {cells}
                    </tr>
                  )}
                  {row.getIsExpanded() &&
                    renderExpandedRow?.(
                      row.original,
                      row.getVisibleCells().length,
                    )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CommonTable;
